import React, { useState, useRef, useEffect } from 'react';

const SORT_NEWEST = 'newest';
const SORT_OLDEST = 'oldest';
const SORT_OPTIONS = [
  { value: SORT_NEWEST, label: 'Mais recentes primeiro' },
  { value: SORT_OLDEST, label: 'Mais antigo primeiro' }
];

/**
 * Detalhe de um post: metadados na sidebar (estimativa, categoria, votantes), conteúdo principal, voto, comentários e campo "Deixe um comentário".
 */
const PostDetailView = ({
  post,
  comments,
  commentValue,
  onCommentChange,
  onSendComment,
  sendCommentLoading = false,
  onVote,
  onBack,
  getStatusLabel,
  replyingToCommentId,
  replyValue,
  onReplyChange,
  onStartReply,
  onSendReply,
  sendReplyLoading = false,
  onCancelReply,
  voters,
  files = []
}) => {
  const voterList = voters !== undefined ? (voters || []) : (post.voters || []);
  const [zoomedFile, setZoomedFile] = useState(null);
  const [commentSort, setCommentSort] = useState(SORT_NEWEST);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const commentsSectionRef = useRef(null);
  const prevSendCommentLoadingRef = useRef(false);

  useEffect(() => {
    if (prevSendCommentLoadingRef.current && !sendCommentLoading && commentsSectionRef.current) {
      commentsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    prevSendCommentLoadingRef.current = sendCommentLoading;
  }, [sendCommentLoading]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
        setSortDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sortLabel = SORT_OPTIONS.find((o) => o.value === commentSort)?.label || 'Mais recentes primeiro';
  const sortedComments = (comments || []).slice().sort((a, b) => {
    const tA = a.created ? new Date(a.created).getTime() : 0;
    const tB = b.created ? new Date(b.created).getTime() : 0;
    return commentSort === SORT_OLDEST ? tA - tB : tB - tA;
  });

  return (
  <div className="hub-post-detail">
    <div className="hub-post-detail-panel">
      <header className="hub-post-detail-header">
        <div className="hub-post-detail-header-main">
          <div
            role="button"
            tabIndex={0}
            className={`hub-post-detail-vote board-clickup-icon-btn-anchor${post.votedByMe ? ' is-voted' : ''}`}
            onClick={(e) => { e.stopPropagation(); onVote && onVote(post); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onVote && onVote(post); } }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onVote && onVote(post); }}
              className="hub-post-detail-vote-btn"
            >
              <i className="bi bi-chevron-up hub-post-detail-vote-icon" aria-hidden />
            </button>
            <span className="hub-post-detail-vote-count">{post.voteCount ?? 0}</span>
            <span className="board-clickup-tooltip board-clickup-tooltip--bottom hub-feedback-tooltip--wrap" role="tooltip">
              {post.votedByMe ? 'Cancelar meu voto neste feedback' : 'Votar neste feedback'}
            </span>
          </div>
          <h1 className="hub-post-detail-title">{post.title}</h1>
        </div>
        <button type="button" onClick={onBack} className="hub-post-detail-back board-clickup-icon-btn-anchor" aria-label="Voltar">
          <i className="bi bi-arrow-left" aria-hidden />
          <span className="board-clickup-tooltip board-clickup-tooltip--bottom hub-feedback-tooltip--wrap" role="tooltip">
            Voltar para listagem de feedbacks
          </span>
        </button>
      </header>

      <div className="hub-post-detail-body">
        <aside className="hub-post-detail-meta">
          {post.categoryName && (
            <div className="hub-post-detail-meta-block">
              <div className="hub-post-detail-meta-label">CATEGORIA</div>
              <div className="hub-post-detail-meta-value">{post.categoryName}</div>
            </div>
          )}
          {post.estimated && (
            <div className="hub-post-detail-meta-block">
              <div className="hub-post-detail-meta-label">PREVISÃO</div>
              <div className="hub-post-detail-meta-value">{post.estimated}</div>
            </div>
          )}
          <div className="hub-post-detail-meta-block">
            <div className="hub-post-detail-meta-label">VOTANTES</div>
            {voterList.length > 0 ? (
              voterList.map((v) => {
                const initial = (v.name && String(v.name).trim()) ? String(v.name).trim().charAt(0).toUpperCase() : '?';
                return (
                  <div key={v.id || v.name} className="hub-post-detail-voter">
                    <div className="hub-post-detail-voter-avatar">{initial}</div>
                    <span className="hub-post-detail-voter-name">{v.name}</span>
                  </div>
                );
              })
            ) : (
              <div className="hub-post-detail-meta-empty">Nenhum votante</div>
            )}
          </div>
        </aside>

        <div className="hub-post-detail-content">
      <div className="hub-post-detail-post-block">
        <div className="hub-post-detail-post-author-row">
          <div className="hub-post-detail-post-avatar">
            {((post.authorName || post.author) && String(post.authorName || post.author).trim()) ? String(post.authorName || post.author).trim().charAt(0).toUpperCase() : '?'}
          </div>
          <span className="hub-post-detail-post-author-name">{post.authorName || post.author}</span>
        </div>
        <p className="hub-post-detail-post-body">{post.details || post.description}</p>
        <div className="hub-muted hub-post-detail-post-date">{post.createdAtLabel || ''}</div>
        {!!files.length && (
          <div className="hub-post-detail-post-files">
            {files.map((file) => (
              <button
                key={file.id || file.key}
                type="button"
                onClick={() => setZoomedFile(file)}
                style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', width: 110, height: 110, display: 'block', background: '#f8fafc', padding: 0, cursor: 'zoom-in' }}
                title="Ampliar imagem"
              >
                <img src={file.viewUrl} alt={file.originalName || 'anexo'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </button>
            ))}
          </div>
        )}
      </div>
      {zoomedFile && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setZoomedFile(null)}
          onKeyDown={(e) => e.key === 'Escape' && setZoomedFile(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(15, 23, 42, 0.78)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'relative', maxWidth: '92vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <button
              type="button"
              onClick={() => setZoomedFile(null)}
              style={{
                alignSelf: 'flex-end',
                padding: '8px 12px',
                border: '1px solid rgba(226, 232, 240, 0.35)',
                background: 'rgba(15, 23, 42, 0.65)',
                color: '#fff',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Voltar ao normal
            </button>
            <img
              src={zoomedFile.viewUrl}
              alt={zoomedFile.originalName || 'anexo ampliado'}
              style={{
                maxWidth: '92vw',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 10,
                border: '1px solid rgba(226, 232, 240, 0.25)',
                background: '#0f172a'
              }}
            />
          </div>
        </div>
      )}
      <div className="hub-post-detail-comment-compose">
        <input
          type="text"
          className="hub-post-detail-comment-input"
          placeholder="Deixe um comentário"
          value={commentValue}
          onChange={(e) => onCommentChange(e.target.value)}
          onKeyDown={(e) => !sendCommentLoading && e.key === 'Enter' && onSendComment()}
          readOnly={sendCommentLoading}
        />
        {commentValue && commentValue.trim() && (
          <div className="hub-post-detail-compose-actions">
            <button
              type="button"
              className="user-form-link-btn user-form-footer-btn"
              onClick={() => onCommentChange && onCommentChange('')}
              disabled={sendCommentLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="user-form-profile-save-btn user-form-footer-btn"
              onClick={() => onSendComment && onSendComment()}
              disabled={sendCommentLoading}
            >
              {sendCommentLoading && (
                <i className="bi bi-arrow-repeat hub-vote-spin" aria-hidden />
              )}
              {sendCommentLoading ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        )}
      </div>
      <div ref={commentsSectionRef}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span className="hub-post-detail-activity-title">Atividade</span>
          <div ref={sortDropdownRef} style={{ position: 'relative' }}>
            <span className="hub-post-detail-sort-label">Ordenar por</span>
            <button
              type="button"
              className="hub-post-detail-sort-btn"
              onClick={() => setSortDropdownOpen((v) => !v)}
            >
              {sortLabel}
              <i className="bi bi-chevron-down" style={{ fontSize: 12 }} />
            </button>
            {sortDropdownOpen && (
              <div className="hub-post-detail-sort-menu">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`hub-post-detail-sort-option${commentSort === opt.value ? ' is-selected' : ''}`}
                    onClick={() => { setCommentSort(opt.value); setSortDropdownOpen(false); }}
                  >
                    {opt.label}
                    {commentSort === opt.value && <i className="bi bi-check" aria-hidden />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(() => {
            const list = sortedComments;
            const roots = list.filter((c) => !c.parentID);
            const getReplies = (parentId) => list.filter((c) => c.parentID === parentId);
            const commentCard = (c, indent, isReply) => (
              <div
                key={c.id}
                className="hub-post-detail-comment-card"
                style={{
                  marginLeft: indent || 0,
                  marginTop: indent ? 8 : 0
                }}
              >
                <div className="hub-post-detail-comment-head">
                  <div className="hub-post-detail-comment-avatar">
                    {((c.authorName || c.author) && String(c.authorName || c.author).trim()) ? String(c.authorName || c.author).trim().charAt(0).toUpperCase() : '?'}
                  </div>
                  <span className="hub-post-detail-comment-author">{c.authorName || c.author}</span>
                  {c.status && (
                    <>
                      <span className="hub-post-detail-comment-status-prefix">alteração de status para</span>
                      <span className="hub-status-pill hub-status-pill--status">
                        {getStatusLabel ? getStatusLabel(c.status) : c.status}
                      </span>
                    </>
                  )}
                </div>
                <p className="hub-post-detail-comment-text">{c.value || c.text}</p>
                <div className="hub-muted" style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#94a3b8' }}>
                  <span style={{ fontSize: 11, fontWeight: 300 }}>{c.createdAtLabel || ''}</span>
                  <button type="button" onClick={() => !sendReplyLoading && onStartReply && onStartReply(c)} disabled={sendReplyLoading} style={{ background: 'none', border: 'none', color: sendReplyLoading ? '#94a3b8' : '#64748b', cursor: sendReplyLoading ? 'not-allowed' : 'pointer', fontSize: '12px', opacity: sendReplyLoading ? 0.7 : 1 }}>Responder</button>
                </div>
                {replyingToCommentId === c.id && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                    <textarea
                      placeholder="Escreva uma resposta..."
                      value={replyValue}
                      onChange={(e) => onReplyChange && onReplyChange(e.target.value)}
                      readOnly={sendReplyLoading}
                      rows={3}
                      style={{ width: '100%', padding: '10px 12px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', resize: 'vertical', marginBottom: 10, opacity: sendReplyLoading ? 0.8 : 1 }}
                    />
                    <div className="hub-post-detail-compose-actions">
                      {onCancelReply && (
                        <button
                          type="button"
                          className="user-form-link-btn user-form-footer-btn"
                          onClick={onCancelReply}
                          disabled={sendReplyLoading}
                        >
                          Cancelar
                        </button>
                      )}
                      <button
                        type="button"
                        className="user-form-profile-save-btn user-form-footer-btn"
                        disabled={!(replyValue && replyValue.trim()) || sendReplyLoading}
                        onClick={() => onSendReply && onSendReply()}
                      >
                        {sendReplyLoading && (
                          <i className="bi bi-arrow-repeat hub-vote-spin" aria-hidden />
                        )}
                        {sendReplyLoading ? 'Enviando...' : 'Enviar'}
                      </button>
                    </div>
                  </div>
                )}
                {!isReply && getReplies(c.id).map((reply) => commentCard(reply, 24, true))}
              </div>
            );
            return roots.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Nenhum comentário ainda.</div>
            ) : (
              roots.map((root) => commentCard(root, 0, false))
            );
          })()}
        </div>
      </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default PostDetailView;
